import React from 'react';
import { Nav4ExpandableMenuItem } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/ui/nav4-expandable-menu-item/index.tsx';
import { Nav4MenuLinkItem } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/ui/nav4-menu-link-item/index.tsx';
import { ENTITY_ID } from '@jira/navigation-apps__sidebar-nav4-context/src/common/constants.tsx';
import ChangesIcon from '@atlaskit/icon/core/changes';
import {
	ExpandableMenuItemContent,
	ExpandableMenuItemTrigger,
} from '@atlaskit/side-nav-items/expandable-menu-item';
import { defineMessage, useIntl } from '@atlassian/jira-intl';
import { fg } from '@atlassian/jira-feature-gating';

type Props = {
	helpCenterId: string;
	projectKey: string;
	baseUrl: string;
	onItemClick: (itemType: string) => void;
};

export const ChannelsExpandableMenuItem = ({
	helpCenterId,
	projectKey,
	baseUrl,
	onItemClick,
}: Props): React.JSX.Element => {
	const { formatMessage } = useIntl();

	return (
		<Nav4ExpandableMenuItem menuId={ENTITY_ID.CUSTOMER_EXPERIENCES_CHANNELS(helpCenterId)}>
			<ExpandableMenuItemTrigger
				interactionName="nav4-customer-experience-channels"
				testId={`customer-experience-menu-channels-${projectKey.toLowerCase()}`}
				elemBefore={<ChangesIcon label="" color="currentColor" />}
			>
				{formatMessage(channelsExpandableItemMessage)}
			</ExpandableMenuItemTrigger>
			<ExpandableMenuItemContent>
				<Nav4MenuLinkItem
					href={`${baseUrl}/support-website/overview`}
					menuId={ENTITY_ID.CUSTOMER_EXPERIENCE_MENU_ITEM(helpCenterId, 'support-website')}
					onClick={() => onItemClick('SupportWebsite')}
					testId={`customer-experience-menu-support-website-${projectKey.toLowerCase()}`}
				>
					{formatMessage(supportWebsiteItemMessage)}
				</Nav4MenuLinkItem>
				<Nav4MenuLinkItem
					href={`${baseUrl}/email`}
					menuId={ENTITY_ID.CUSTOMER_EXPERIENCE_MENU_ITEM(helpCenterId, 'email')}
					onClick={() => onItemClick('Email')}
					testId={`customer-experience-menu-email-${projectKey.toLowerCase()}`}
				>
					{formatMessage(emailItemMessage)}
				</Nav4MenuLinkItem>
				<Nav4MenuLinkItem
					href={`${baseUrl}/embed`}
					menuId={ENTITY_ID.CUSTOMER_EXPERIENCE_MENU_ITEM(helpCenterId, 'embed')}
					onClick={() => onItemClick('Embed')}
					testId={`customer-experience-menu-embed-${projectKey.toLowerCase()}`}
				>
					{formatMessage(embedItemMessage)}
				</Nav4MenuLinkItem>
				<Nav4MenuLinkItem
					href={`${baseUrl}/phone`}
					menuId={ENTITY_ID.CUSTOMER_EXPERIENCE_MENU_ITEM(helpCenterId, 'phone')}
					onClick={() => onItemClick('Voice')}
					testId={`customer-experience-menu-phone-${projectKey.toLowerCase()}`}
				>
					{formatMessage(voiceItemMessage)}
				</Nav4MenuLinkItem>
				{fg('csm-omni-whatsapp-support') && (
					<Nav4MenuLinkItem
						href={`${baseUrl}/messaging-apps`}
						menuId={ENTITY_ID.CUSTOMER_EXPERIENCE_MENU_ITEM(helpCenterId, 'messaging-apps')}
						onClick={() => onItemClick('MessagingApps')}
						testId={`customer-experience-menu-messaging-apps-${projectKey.toLowerCase()}`}
					>
						{formatMessage(messagingAppsItemMessage)}
					</Nav4MenuLinkItem>
				)}
			</ExpandableMenuItemContent>
		</Nav4ExpandableMenuItem>
	);
};

const channelsExpandableItemMessage = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-customer-service.customer-experience-menu.channels-expandable-item',
	defaultMessage: 'Channels',
	description: 'Channels expandable item in customer experience menu',
});

const supportWebsiteItemMessage = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-customer-service.customer-experience-menu.support-website-item',
	defaultMessage: 'Support website',
	description: 'Support website link item in customer experience menu',
});

const emailItemMessage = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-customer-service.customer-experience-menu.email-item',
	defaultMessage: 'Email',
	description: 'Email link item in customer experience menu',
});

const embedItemMessage = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-customer-service.customer-experience-menu.embed-item',
	defaultMessage: 'Embed',
	description: 'Embed link item in customer experience menu',
});

const voiceItemMessage = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-customer-service.customer-experience-menu.voice-item',
	defaultMessage: 'Voice',
	description: 'Voice link item in customer experience menu',
});

const messagingAppsItemMessage = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-customer-service.customer-experience-menu.messaging-apps-item',
	defaultMessage: 'Messaging apps',
	description: 'Messaging apps link item in customer experience menu',
});
